import _ from "lodash"
import moment from "moment"
import uniqolor from "uniqolor"
import { filesize } from "filesize"
import { trans, toLocalDateFormat } from '@utils'
import { getSuitableData } from "../utility/utils"

export const AreaChart = (data, dateRange, context, color, options, isDecimal, calculateAverageInGrouping, height, exportOptions) => {
  const formattedData = getSuitableData(data, dateRange, calculateAverageInGrouping)
  return {
    series: [{name: exportOptions?.seriesName || 'value', data: _.map(formattedData, x => x.value)}],
    options: _.merge({
      chart: {
        height,
        toolbar: {
          show: false,
          export: {
            csv: {
              filename: exportOptions?.filename || undefined,
              headerCategory: exportOptions?.csv?.headerCategory || 'category',
              headerValue: exportOptions?.csv?.headerValue || 'value',
              dateFormatter(timestamp) {
                return toLocalDateFormat(timestamp)
              }
            },
            svg: {
              filename: exportOptions?.filename || undefined
            },
            png: {
              filename: exportOptions?.filename || undefined
            }
          }
        },
        sparkline: {
          enabled: true
        },
        animations: {
          enabled: false
        }
      },
      grid: {
        show: true
      },
      colors: [_.get(context, `colors.${color}.main`)],
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        width: 2.5
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 0.9,
          opacityFrom: 0.7,
          opacityTo: 0.5,
          stops: [0, 80, 100]
        }
      },
      xaxis: {
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        }
      },
      yaxis: {
        decimalsInFloat: isDecimal ? 2 : 0,
        labels: {
          show: false
        }
      },
      tooltip: {
        x: {
          show: true,
          formatter: (value, { series, seriesIndex, dataPointIndex, w }) => {
            return _.get(formattedData, `${dataPointIndex}.date`)
          }
        },
        y: {
          formatter: undefined,
          title: {
            formatter: (value, { series, seriesIndex, dataPointIndex, w }) => {
              return ''
            }
          }
        }
      }
    }, options)
  }
}

export const BarChart = (data, dateRange, setModal, context, color, options, detailsType, title, ToolbarDetailsComponent, DetailsComponent, isDecimal, calculateAverageInGrouping, height, exportOptions) => {
  const formattedData = getSuitableData(data, dateRange, calculateAverageInGrouping)
  return {
    series: [{name: exportOptions?.seriesName || 'value', data: _.map(formattedData, x => x.value)}],
    options: {
      chart: {
        toolbar: {
          show: false,
          autoSelected: 'pan',
          tools: {
            download: false
          },
          export: {
            csv: {
              filename: exportOptions?.filename || undefined,
              headerCategory: exportOptions?.csv?.headerCategory || 'category',
              headerValue: exportOptions?.csv?.headerValue || 'value',
              dateFormatter(timestamp) {
                return toLocalDateFormat(timestamp)
              }
            },
            svg: {
              filename: exportOptions?.filename || undefined
            },
            png: {
              filename: exportOptions?.filename || undefined
            }
          }
        },
        events: {
          click: (event, chartContext, config) => {
            if (_.get(formattedData, `${config.dataPointIndex}`)) {
              switch (detailsType) {
                case 'modal':
                  setModal({show: true, data: {item: _.get(formattedData, `${config.dataPointIndex}`), title, ToolbarDetailsComponent, DetailsComponent}})
                  break
                default:
                  break
              }
            }
            // The last parameter config contains additional information like `seriesIndex` and `dataPointIndex` for cartesian charts
          }
        },
        height,
        type: 'bar',
        stacked: true,
        parentHeightOffset: 0,
        animations: {
          enabled: false
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (value, { series, seriesIndex, dataPointIndex, w }) => `${value}`,
          title: {
            formatter: () => ''
          }
        }
      },
      plotOptions: {
        bar: {
          columnWidth: '15%',
          colors: {
            // backgroundBarColors: _.times(_.size(data), x => `${_.get(context, `colors.${color}.main`)}50`),
            // backgroundBarRadius: 10
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      legend: {
        position: 'top',
        horizontalAlign: 'start'
      },
      colors: [_.get(context, `colors.${color}.main`)],
      stroke: {
        show: true,
        colors: ['transparent']
      },
      grid: {
        xaxis: {
          lines: {
            show: true
          }
        }
      },
      xaxis: {
        categories: _.map(formattedData, x => x.date),
        type: 'datetime'
      },
      fill: {
        opacity: 1
      },
      yaxis: {
        // decimalsInFloat: isDecimal ? 2 : 0,
        // opposite: false,
        tickAmount: 1,
        labels: {
          show: true,
          formatter: (val) => {
            return val.toFixed(isDecimal ? 2 : 0)
          }
        }
      },
      ...options
    }
  }
}

export const MultiLineChart = (data, dateRange, setModal, context, color, options, detailsType, title, ToolbarDetailsComponent, DetailsComponent, height, exportOptions, extraProps) => {
  const labels = data?.labels || []
  const series = data?.series || []

  return {
    series,
    options: {
      noData: {
        text: extraProps?.noData?.text || trans("gen.datatable.nodDataFound"),
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
          color: "#6e6b7b", // FW-1902 — was #ff0000, which read as an error state
          fontSize: '18px',
          fontFamily: undefined
        }
      },
      chart: {
        toolbar: {
          show: false,
          autoSelected: 'pan',
          tools: {
            download: false
          },
          export: {
            csv: {
              filename: exportOptions?.filename || undefined,
              headerCategory: exportOptions?.csv?.headerCategory || 'category',
              headerValue: exportOptions?.csv?.headerValue || 'value',
              dateFormatter(timestamp) {
                return toLocalDateFormat(timestamp)
              }
            },
            svg: {
              filename: exportOptions?.filename || undefined
            },
            png: {
              filename: exportOptions?.filename || undefined
            }
          }
        },
        height,
        type: 'line',
        stacked: false
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      markers: {
        size: 5, // Adjust the size of the marker
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: {
          size: 7 // Set size of marker when hovered, if needed
        }
      },
      xaxis: {
        type: 'datetime',
        categories: labels,
        labels: {
          formatter: (val, timestamp,  opts) => {
            if (opts.i === 0) return ''
            if (_.size(series) > 0) {
              return moment(timestamp).format("MMM D YY H:mm")
            }
            return undefined
          }
        },
        crosshairs: {
          show: true,
          width: 1,
          opacity: 0
        },
        tooltip: {
          enabled: false
        }
      },
      tooltip: { 
        enabled: true,
        shared: false,
        intersect: true,
        custom: (args) => {
          const {seriesIndex, dataPointIndex, w} = args 
          const date = args.w.config.series[seriesIndex]?.data[dataPointIndex]?.x
          const value = args.w.config.series[seriesIndex]?.data[dataPointIndex]?.y
          const question = args.w.config.series[seriesIndex]?.data[dataPointIndex]?.question
          const asset = args.w.config.series[seriesIndex]?.data[dataPointIndex]?.asset
          const color = args.w.config.series[seriesIndex]?.color

          return `
            <div class="card mb-0">
              <div class="card-header d-flex justify-content-between p-50 bg-light">
                <span>
                  <span style="display: inline-flex; width: 10px; height: 10px; border-radius: 5px; background-color: ${color}"></span>
                  <span class="text-dark ml-25">${date}</span> 
                </span>
                <span class="ml-1 text-dark">
                  <span>Value:</span>
                  <span>${value}</span>
                </span>
              </div>
              <div class="card-body p-50">
                <div class="font-weight-bold font-medium-2">${asset || ''}</div>
                <div class="text-wrap" style="width: 250px;">${question || ''}</div>
              </div>
            </div>
          `
        }
      },
      legend: { 
        show: true,
        horizontalAlign: 'left',
        fontSize: '14px',
        height: undefined,
        itemMargin: {
          vertical: 10
        }
      }
    }
  }
}

export const HorizontalBarChart = (data, setModal, context, color, options, detailsType, title, ToolbarDetailsComponent, DetailsComponent, height, exportOptions) => {
  return {
    series: [{name: trans('analytics.issues.issuesCount'), data: _.map(data, x => x.issues_count)}],
    options: {
      noData: {
        text: trans("gen.datatable.nodDataFound"),
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
          color: "#6e6b7b", // FW-1902 — was #ff0000, which read as an error state
          fontSize: '18px',
          fontFamily: undefined
        }
      },
      chart: {
        toolbar: {
          show: false,
          export: {
            csv: {
              filename: exportOptions?.filename || undefined,
              headerCategory: exportOptions?.csv?.headerCategory || 'category',
              headerValue: exportOptions?.csv?.headerValue || 'value',
              dateFormatter(timestamp) {
                return toLocalDateFormat(timestamp)
              }
            },
            svg: {
              filename: exportOptions?.filename || undefined
            },
            png: {
              filename: exportOptions?.filename || undefined
            }
          }
        },
        events: {
          click: (event, chartContext, config) => {
            if (_.get(data, `${config.dataPointIndex}`)) {
              switch (detailsType) {
                case 'modal':
                  const item = _.get(data, `${config.dataPointIndex}`)
                  setModal({show: true, data: {item: {...item, filterKeys: {asset: item.asset_id}}, title: trans('analytics.nav.issues'), ToolbarDetailsComponent, DetailsComponent}})
                  break
                default:
                  break
              }
            }
            // The last parameter config contains additional information like `seriesIndex` and `dataPointIndex` for cartesian charts
          }
        },
        height,
        type: 'bar',
        stacked: true,
        parentHeightOffset: 0,
        animations: {
          enabled: false
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
            horizontal: true
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: _.map(data, x => x.asset_asset_id)
      },
      ...options
    }
  }
}

export const MixChart = (data, dateRange, setModal, context, color, options, detailsType, title, ToolbarDetailsComponent, DetailsComponent, height, exportOptions) => {
  const average_score = _.map(data, x => ({date: x.date, value: x.average_score}))
  const formattedAverageScore = getSuitableData(average_score, dateRange, true)
  const issues = _.map(data, x => ({date: x.date, value: x.issues}))
  const formattedIssues = getSuitableData(issues, dateRange)
  const total = _.map(data, x => ({date: x.date, value: x.total}))
  const formattedTotal = getSuitableData(total, dateRange)
  return {
    series: [
      {
        name: trans('analytics.mix.inspections'),
        type: 'column',
        data: _.map(formattedTotal, x => x.value)
      }, {
        name: trans('analytics.actions.averageScore'),
        type: 'area',
        data: _.map(formattedAverageScore, x => x.value)
      }, {
        name: trans('analytics.nav.issues'),
        type: 'line',
        data: _.map(formattedIssues, x => x.value)
      }
    ],
    options: {
      // title: { text:  22},
      // subtitle: {text: title},
      noData: {
        text: trans("gen.datatable.nodDataFound"),
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
          color: "#6e6b7b", // FW-1902 — was #ff0000, which read as an error state
          fontSize: '18px',
          fontFamily: undefined
        }
      },
      chart: {
        events: {
          click: (event, chartContext, config) => {
            if (_.get(formattedTotal, `${config.dataPointIndex}`) && _.get(formattedTotal, `${config.dataPointIndex}.value`) > 0) {
              switch (detailsType) {
                case 'modal':
                  setModal({show: true, data: {item: {..._.get(formattedAverageScore, `${config.dataPointIndex}`), ..._.get(formattedIssues, `${config.dataPointIndex}`), ..._.get(formattedTotal, `${config.dataPointIndex}`)}, title, ToolbarDetailsComponent, DetailsComponent}})
                  break
                default:
                  break
              }
            }
            // The last parameter config contains additional information like `seriesIndex` and `dataPointIndex` for cartesian charts
          }
        },
        toolbar: {
          show: false,
          autoSelected: 'pan',
          tools: {
            download: false
          },
          export: {
            csv: {
              filename: exportOptions?.filename || undefined,
              headerCategory: exportOptions?.csv?.headerCategory || 'category',
              headerValue: exportOptions?.csv?.headerValue || 'value',
              dateFormatter(timestamp) {
                return toLocalDateFormat(timestamp)
              }
            },
            svg: {
              filename: exportOptions?.filename || undefined
            },
            png: {
              filename: exportOptions?.filename || undefined
            }
          }
        },
        height,
        type: 'line',
        stacked: false
      },
      colors: ["#008FFB", "#00E396", "#ff0000"],
      stroke: {
        width: [0, 2.5, 2.5],
        curve: 'smooth'
      },
      plotOptions: {
        bar: {
          columnWidth: '50%'
        }
      },
      fill: {
        opacity: [0.85, 0.25, 1],
        gradient: {
          inverseColors: false,
          shade: 'light',
          type: "vertical",
          opacityFrom: 0.85,
          opacityTo: 0.55,
          stops: [0, 100, 100, 100]
        }
      },
      labels: _.map(formattedIssues, x => x.date),
      markers: {
        size: 0
      },
      xaxis: {
        type: 'datetime'
      },
      yaxis: [
        {
          decimalsInFloat: 0,
          axisTicks: {
            show: true
          },
          axisBorder: {
            show: true,
            color: '#008FFB'
          },
          labels: {
            style: {
              colors: '#008FFB'
            },
            formatter(val, index) {
              return val.toFixed(0)
            }
          },
          title: {
            text: "",
            style: {
              color: '#008FFB'
            }
          },
          tooltip: {
            enabled: true
          }
        },
        {
          seriesName: 'Income',
          opposite: true,
          decimalsInFloat: 0,
          axisTicks: {
            show: true
          },
          axisBorder: {
            show: true,
            color: '#00E396'
          },
          labels: {
            style: {
              colors: '#00E396'
            },
            formatter(val, index) {
              return `${val.toFixed(2)}%`
            }
          },
          title: {
            text: "",
            style: {
              color: '#00E396'
            }
          }
        },
        {
          seriesName: 'Revenue',
          opposite: true,
          decimalsInFloat: 0,
          axisTicks: {
            show: true
          },
          axisBorder: {
            show: true,
            color: '#ff0000'
          },
          labels: {
            style: {
              colors: '#ff0000'
            },
            formatter(val, index) {
              return val.toFixed(0)
            }
          },
          title: {
            text: "",
            style: {
              color: '#ff0000'
            }
          }
        }
      ],
      tooltip: {
        shared: true,
        intersect: false,
        y: [
          {
            formatter (y) {
              if (typeof y !== "undefined") {
                return `${y.toFixed(0)}`
              }
              return y
            }
          },
          {
            formatter (y) {
              if (typeof y !== "undefined") {
                return `${y.toFixed(2)}%`
              }
              return y
            }
          },
          {
            formatter (y) {
              if (typeof y !== "undefined") {
                return `${y.toFixed(0)}`
              }
              return y
            }
          }
        ]
      }
    }
  }
}

export const DonutChart = (formattedData, setModal, detailsType, title, ToolbarDetailsComponent, DetailsComponent, isDecimal, height, exportOptions) => {
  const colors = formattedData[0].color ? {
    colors: _.map(formattedData, x => x.color)
  } : {}
  return {
    series: _.map(formattedData, x => x.value),
    options: {
      chart:{
        height,
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (_.get(formattedData, `${config.dataPointIndex}`) && event) {
              switch (detailsType) {
                case 'modal':
                  setModal({show: true, data: {item: _.get(formattedData, `${config.dataPointIndex}`), title: trans('analytics.nav.issues'), ToolbarDetailsComponent, DetailsComponent}})
                  break
                default:
                  break
              }
            }
          }
        },
        toolbar: {
          export: {
            csv: {
              filename: exportOptions?.filename || undefined,
              headerCategory: exportOptions?.csv?.headerCategory || 'category',
              headerValue: exportOptions?.csv?.headerValue || 'value'
            },
            svg: {
              filename: exportOptions?.filename || undefined
            },
            png: {
              filename: exportOptions?.filename || undefined
            }
          }
        },
        type:'pie'
        // width: 380
      },
      legend: {
        show: true,
        position: 'bottom'
      },
      labels: _.map(formattedData, x => trans(`analytics.donut.${x.name}`)),
      ...colors,
      plotOptions: {
        pie: {
          dataLabels: {
            offset: -10,
            minAngleToShowLabel: 10
          }
        }
      },
      yaxix:{
        decimalsInFloat: isDecimal ? 2 : 0
      },
      responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200
          },
          legend: {
            position: 'bottom'
          }
        }
      }
      ]
    }
  }
}

export const StorageChart = (data, dataRange, context, color, options, isDecimal, calculateAverageInGrouping, height, exportOptions, extraProps) => {
  return {
    series: _.map(data, (item) => item.size),
    options: _.merge({
      chart: {
        height,
        toolbar: {
          show: false
        }
      },
      labels: _.map(data, (item) => item.label),
      dataLabels: {
        enabled: false
      },
      legend: { show: false },
      comparedResult: [2, -3, 8],
      stroke: { width: 0 },
      colors: _.map(data, (item) => uniqolor(item.label, { saturation: 60, lightness: [30, 70] }).color),
      tooltip: {
        enabled: true,
        y: {
          formatter(val) {
            return `${filesize(val * 1024, {standard: "jedec"})}` 
          }
        }
      },
      plotOptions: {
        pie: {
          startAngle: -10,
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: {
                offsetY: 0
              },
              value: {
                offsetY: 10,
                fontSize: '16px',
                fontWeight: 700,
                formatter(val, o) {
                  if (!isNaN(Number(val))) {
                    return `${filesize(val * 1024, {standard: "jedec"})}`
                  }
                  return val
                }
              },
              total: {
                show: true,
                offsetY: 15,
                label: trans('analytics.storage.totalUsed'),
                fontSize: '18px',
                formatter(w) {
                  const total = w.globals.seriesTotals.reduce((a, b) => {
                    return a + b
                  }, 0)
                  return `${filesize(total * 1024, {standard: "jedec"})}`
                }
              }
            }
          }
        }
      }
    }, options)
  }
}
